/**
 * Centralized user DISPLAY preferences for the SPA — time format
 * (12h AM/PM vs 24h), date style (short/medium/long), and number
 * compactness. Persisted on the backend via
 * /api/profile/display-prefs (UserAccount.DisplayPrefsJson).
 *
 * Pattern mirrors the existing notification prefs:
 *   * Backend round-trips opaque JSON — adding a new preference is
 *     UI-only, no backend deploy required.
 *   * Module-scoped singleton state — `useDisplayPrefs()` everywhere
 *     reads the same ref, so changing the time format on /settings/
 *     display causes every render across the app to update.
 *   * useFormatDate() consumes this composable, so any component
 *     that formats a timestamp via that helper automatically honors
 *     the user's choice.
 *
 * Cold-start sequence:
 *   1. SSR / first render → state has DEFAULTS (no fetch, instant
 *      first paint with sensible US-flavored defaults).
 *   2. On client, `ensureLoaded()` fires once per session and merges
 *      the persisted JSON over defaults.
 *   3. `save()` writes back to the backend and updates state on
 *      success.
 *
 * Optimistic local updates: the UI doesn't wait for the save round-
 * trip before reflecting a change — the local state is patched
 * immediately, then save() runs in the background. If the save fails
 * the page surfaces a toast; the local change stays so the user can
 * retry without losing their toggle. This matches the pattern in
 * settings/notifications.vue (which uses an explicit Save button).
 */

import { ref, readonly, watch } from 'vue'

export type TimeFormat   = '12h' | '24h'
export type DateStyle    = 'short' | 'numeric' | 'iso' | 'medium' | 'long' | 'full'
export type Theme        = 'light' | 'dark' | 'system'
export type FontSize     = 'small' | 'normal' | 'large' | 'extraLarge'
export type FontFamily   = 'system' | 'serif' | 'mono'
export type FirstDayOfWeek = 'sunday' | 'monday'
export type TableDensity = 'compact' | 'normal' | 'comfortable'

export interface DisplayPrefs {
  // ===== Appearance =====
  /**
   * Color scheme. `system` follows the OS / browser preference; `light`
   * and `dark` pin explicitly. Wired through @nuxtjs/color-mode so
   * Nuxt UI Pro components respect the choice on the next render.
   */
  theme: Theme

  /**
   * Reduces / removes UI motion (transitions, fade-ins, hover
   * animations). Adds the `reduce-motion` class on the root element
   * so CSS rules can opt out of animations. Captures the user's
   * preference even when their OS already requests
   * `prefers-reduced-motion: reduce` — gives a manual override.
   */
  reducedMotion: boolean

  /**
   * High-contrast mode. Adds a `high-contrast` class on the root for
   * stylesheets to opt into stronger borders / colors. Captured for
   * future styling work — currently no CSS rules consume it.
   */
  highContrast: boolean

  /**
   * Pin the page section-nav bar to the top while the page content
   * scrolls underneath it (household tabs, the settings
   * Notifications/Display/Security nav, the admin nav). On by default.
   * Consumed by useStickyNavToolbarUi(), which the shared DashboardMenu
   * and the settings/admin toolbars bind to their root `:ui`. Off → the
   * nav scrolls away with the page (the original behavior).
   */
  stickyNav: boolean

  /**
   * When `stickyNav` is on, pin the WHOLE page header (title + value +
   * nav) instead of just the nav bar. Only affects pages that have a
   * title above the nav (household / sleeve / registration); on settings
   * and admin the header IS the nav, so this has no extra effect. Ignored
   * entirely when `stickyNav` is off. Default off (= pin the nav bar only).
   */
  stickyHeader: boolean

  // ===== Typography =====
  /**
   * Base font size of the SPA. Sets the root font-size via the
   * `font-size-N` class, which rescales every `rem`-based dimension
   * proportionally — text, spacing, button heights, etc.
   *   small      → 14px
   *   normal     → 16px (default)
   *   large      → 18px
   *   extraLarge → 20px
   */
  fontSize: FontSize

  /**
   * Font family preference. `system` keeps the existing UI font stack;
   * `serif` switches to a serif body for reading; `mono` is a
   * monospaced surface for users who prefer fixed-width type. Captured
   * but not yet wired into the global stylesheet.
   */
  fontFamily: FontFamily

  // ===== Formatting =====
  /**
   * Whether timestamps render as 12-hour (`2:32 PM`) or 24-hour
   * (`14:32`). Default `12h` because the portal's primary audience
   * is US-based.
   */
  timeFormat: TimeFormat

  /**
   * Length of the date portion when both date + time are shown.
   *   * `short`  → numeric, locale-driven (e.g. 5/21/26)
   *   * `medium` → abbreviated month (May 21, 2026) — the default,
   *               matches the existing inline calls to the format
   *               helpers.
   *   * `long`   → full month name (May 21, 2026)  — currently
   *               the same as medium in most locales, kept for
   *               future-proofing.
   */
  dateStyle: DateStyle

  /**
   * Whether large numbers render compactly ($1.2M vs $1,200,000).
   * Disabled by default — portfolio clients usually want exact
   * figures; this is an opt-in for advisors who scan dashboards.
   * Reserved for future use; not yet consumed by table renderers.
   */
  compactNumbers: boolean

  /**
   * Which day a week starts on in the calendar / date pickers.
   * `sunday` is the US convention; `monday` is ISO.
   */
  firstDayOfWeek: FirstDayOfWeek

  // ===== Tables =====
  /**
   * Row height for data tables. `compact` is the legacy density; the
   * code currently bakes in `h-[56px]` for `normal`, so this is
   * captured but not yet wired to a CSS class.
   */
  tableDensity: TableDensity

  /**
   * Default page size for paginated tables (cont/dist, holdings,
   * etc.). Pages that already own pagination state still win for
   * the duration of a session, but new visits to a table-page read
   * this value as the starting page size.
   */
  defaultTablePageSize: number

  /**
   * Always render the "Showing X to Y of Z entries" count in table footers,
   * even when a table fits on one page (no paginator). Off by default — most
   * tables are short and the count is just noise; advisors who want the row
   * tally regardless can flip it on.
   */
  alwaysShowRowCount: boolean

  /**
   * Show the hover row-preview popup on the desktop allocation tables (the
   * registration list under the colour bar). Off by default — it can get in
   * the way; users who like it can switch it on.
   */
  tableHoverPreview: boolean

  /**
   * Show cell + column "crosshair" guide lines on data tables. Hovering
   * (desktop) or tapping (mobile) a cell tints its row and its column so you
   * can trace a value back to its row/column heading. Subtle by design and
   * ON by default. Consumed by TableContextMenu — the shared wrapper around
   * every data table — so flipping it lights up guides everywhere at once.
   */
  tableCrosshair: boolean

  /**
   * Show the left/right scroll chevrons + edge fade on overflowing tab / nav
   * rows (ScrollFade). ON by default.
   *
   * It was off on the theory that content bleeding off the right edge is signal
   * enough. It isn't: on a trackpad or a narrow desktop window there is no
   * swipe affordance and nothing indicates the row scrolls at all, so tabs past
   * the fold are simply undiscoverable. The chevrons cost a few pixels and are
   * hidden automatically when the row doesn't overflow.
   */
  showTabOverflowArrows: boolean
}

const DEFAULTS: DisplayPrefs = {
  theme:                'system',
  reducedMotion:        false,
  highContrast:         false,
  stickyNav:            true,
  stickyHeader:         false,
  fontSize:             'normal',
  fontFamily:           'system',
  timeFormat:           '12h',
  dateStyle:            'medium',
  compactNumbers:       false,
  firstDayOfWeek:       'sunday',
  tableDensity:         'normal',
  defaultTablePageSize: 100,
  alwaysShowRowCount:   false,
  tableHoverPreview:    false,
  tableCrosshair:       false,
  showTabOverflowArrows: true
}

// Module-scoped singletons. Importing this composable from anywhere
// in the app sees the same state — that's what lets a Settings page
// toggle affect every existing render instantly.
const state    = ref<DisplayPrefs>({ ...DEFAULTS })
const loading  = ref(false)
const loaded   = ref(false)
const saveErr  = ref<string | null>(null)
let inflight: Promise<void> | null = null

function pickEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback
}

function mergeOverDefaults(remote: Partial<DisplayPrefs> | null | undefined): DisplayPrefs {
  if (!remote || typeof remote !== 'object') return { ...DEFAULTS }
  const pageSize = typeof remote.defaultTablePageSize === 'number' && remote.defaultTablePageSize > 0
    ? Math.min(Math.floor(remote.defaultTablePageSize), 200)
    : DEFAULTS.defaultTablePageSize
  return {
    theme:                pickEnum(remote.theme,          ['light', 'dark', 'system'] as const,                            DEFAULTS.theme),
    reducedMotion:        !!remote.reducedMotion,
    highContrast:         !!remote.highContrast,
    // Default-on: only an explicit stored `false` turns the pinned nav off,
    // so existing users (no stored value) get the new pinned behavior.
    stickyNav:            typeof remote.stickyNav === 'boolean' ? remote.stickyNav : DEFAULTS.stickyNav,
    stickyHeader:         typeof remote.stickyHeader === 'boolean' ? remote.stickyHeader : DEFAULTS.stickyHeader,
    fontSize:             pickEnum(remote.fontSize,       ['small', 'normal', 'large', 'extraLarge'] as const,             DEFAULTS.fontSize),
    fontFamily:           pickEnum(remote.fontFamily,     ['system', 'serif', 'mono'] as const,                            DEFAULTS.fontFamily),
    timeFormat:           pickEnum(remote.timeFormat,     ['12h', '24h'] as const,                                         DEFAULTS.timeFormat),
    dateStyle:            pickEnum(remote.dateStyle,      ['short', 'numeric', 'iso', 'medium', 'long', 'full'] as const,  DEFAULTS.dateStyle),
    compactNumbers:       !!remote.compactNumbers,
    firstDayOfWeek:       pickEnum(remote.firstDayOfWeek, ['sunday', 'monday'] as const,                                   DEFAULTS.firstDayOfWeek),
    tableDensity:         pickEnum(remote.tableDensity,   ['compact', 'normal', 'comfortable'] as const,                   DEFAULTS.tableDensity),
    defaultTablePageSize: pageSize,
    alwaysShowRowCount:   !!remote.alwaysShowRowCount,
    tableHoverPreview:    !!remote.tableHoverPreview,
    // Default-on: only an explicit stored `false` turns the guides off, so
    // existing users (no stored value) get the new on-by-default behavior.
    tableCrosshair:       typeof remote.tableCrosshair === 'boolean' ? remote.tableCrosshair : DEFAULTS.tableCrosshair,
    showTabOverflowArrows: !!remote.showTabOverflowArrows
  }
}

/**
 * Push the prefs that actually drive the DOM into the document root —
 * theme class (light/dark), font-size class (small/normal/large/xl),
 * reduced-motion class, high-contrast class, font-family class.
 *
 * Called every time `state` changes (via the watcher below) so
 * toggling a prefence on the Display page is reflected immediately,
 * no reload required.
 *
 * Theme also syncs through Nuxt UI Pro's color-mode composable so
 * components that read `colorMode.value` directly stay in sync.
 */
function applyToDocument(p: DisplayPrefs): void {
  if (import.meta.server || typeof document === 'undefined') return
  const root = document.documentElement

  // --- font-size: rescales all rem-based dimensions in the SPA. ---
  root.classList.remove('font-size-small', 'font-size-normal', 'font-size-large', 'font-size-extraLarge')
  root.classList.add(`font-size-${p.fontSize}`)

  // --- font-family: applied via a body class. ---
  root.classList.remove('font-family-system', 'font-family-serif', 'font-family-mono')
  root.classList.add(`font-family-${p.fontFamily}`)

  // --- reduced motion / high contrast: opt-in CSS rules consume these. ---
  root.classList.toggle('reduce-motion',  p.reducedMotion)
  root.classList.toggle('high-contrast',  p.highContrast)

  // --- theme: NOT yet applied. A dark stylesheet hasn't been built
  //     for the SPA, so flipping the `dark` class on <html> would just
  //     surface half-broken Tailwind dark: rules from third-party
  //     components without a coherent palette. The preference is
  //     still saved (so the toggle survives) and surfaces with a
  //     "Coming soon" badge on the settings page. When the dark
  //     stylesheet ships, re-enable this block.
  //
  // try {
  //   const cm = useColorMode()
  //   if (cm.preference !== p.theme) cm.preference = p.theme
  // } catch {
  //   root.classList.toggle('dark', p.theme === 'dark')
  // }
}

async function ensureLoaded(): Promise<void> {
  // SSR has no usable per-user prefs (the auth cookie isn't reliably
  // forwarded to BFF fetches from inside composables); we defer the
  // load to client-side only and fall back to DEFAULTS in the meantime.
  if (import.meta.server) return
  if (loaded.value) return
  if (inflight) return inflight

  loading.value = true
  inflight = (async () => {
    try {
      const remote: any = await $fetch('/api/profile/display-prefs', { credentials: 'include' })
      state.value = mergeOverDefaults(remote)
      loaded.value = true
    } catch {
      // Silent fallback. The user gets defaults; they can re-save on
      // the Display settings page to retry. Anything noisier on cold
      // start would put a toast on every page load if the backend
      // is briefly unreachable.
    } finally {
      loading.value = false
      inflight = null
      // Whichever way the load ended (success or fallback to
      // defaults), push the resulting prefs into the document so the
      // class/theme/font-size states are correct for the first paint.
      applyToDocument(state.value)
    }
  })()
  return inflight
}

// Watcher: every time the module-scoped state changes (initial load,
// successful save, programmatic mutation) push the change into the
// document. Set up lazily on first useDisplayPrefs() call from the
// client so we don't try to set up reactivity during SSR.
let domSyncStarted = false
function startDomSync() {
  if (domSyncStarted) return
  if (import.meta.server) return
  domSyncStarted = true
  watch(state, (next) => applyToDocument(next), { deep: true, immediate: true })
}

async function save(patch: Partial<DisplayPrefs>): Promise<void> {
  saveErr.value = null
  const next = { ...state.value, ...patch }
  // Optimistic update — the SPA reflects the change instantly so the
  // user sees their toggle take effect immediately. If the save
  // fails we keep the local state (so they can retry) but expose
  // `saveError` for the page to surface.
  state.value = next
  try {
    // CSRF header is required by the global double-submit-cookie
    // middleware on every mutating request. Reading the token from
    // useAuth() (which exposes the cookie value) keeps it in sync
    // with whatever the cookie says; falling back to '' means a
    // mid-rotation tab still attempts the call rather than throwing
    // before the network even gets touched.
    const csrf = useAuth().csrf.value || ''
    await $fetch('/api/profile/display-prefs', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'x-csrf': csrf },
      body: { prefs: next }
    })
    loaded.value = true
  } catch (e: any) {
    saveErr.value = e?.data?.statusMessage || e?.message || 'Could not save preferences'
    throw e
  }
}

/**
 * Force a re-fetch — useful after a user signs out / in as a
 * different identity in the same tab, so the previous user's
 * cached prefs don't leak through. Most pages don't need to
 * call this; the layout's auth lifecycle is the natural site.
 */
async function refresh(): Promise<void> {
  loaded.value = false
  inflight = null
  await ensureLoaded()
}

export function useDisplayPrefs() {
  // Kick off the load on first use without awaiting — pages don't
  // need to block on it. The state starts at DEFAULTS, then updates
  // reactively when the fetch resolves; consumers re-render then.
  if (!loaded.value && !inflight && import.meta.client) {
    void ensureLoaded()
  }
  // Lazy-start the DOM watcher on first client call. The check inside
  // is idempotent so calling useDisplayPrefs() from N components only
  // sets up the watch once.
  startDomSync()
  return {
    state:    readonly(state),
    loading:  readonly(loading),
    loaded:   readonly(loaded),
    saveError: readonly(saveErr),
    save,
    refresh,
    ensureLoaded
  }
}
